/** Extra time a beat may add on top of the animation that already plays. */
export const BEAT_PAD_CAP_MS = 150;

export type JuiceKind = 'match3' | 'match4' | 'match5' | 'cascade' | 'special' | 'moment';

let forcedReduced: boolean | null = null;

/** Tests pin this. The game reads `prefers-reduced-motion` when it is left unset. */
export function setReducedMotionForTests(value: boolean | null): void {
  forcedReduced = value;
}

export function prefersReducedMotion(): boolean {
  if (forcedReduced !== null) return forcedReduced;
  if (typeof matchMedia !== 'function') return false;
  try {
    return matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export function matchJuice(size: number): JuiceKind {
  if (size >= 5) return 'match5';
  if (size >= 4) return 'match4';
  return 'match3';
}

/**
 * How long to hold after the tiles have already started moving.
 * Match-3 adds nothing. Everything else stays at or under 150ms.
 * Reduced motion adds nothing.
 */
export function beatPadMs(kind: JuiceKind, magnitude = 1): number {
  if (prefersReducedMotion()) return 0;
  let ms = 0;
  if (kind === 'match4') ms = 50;
  else if (kind === 'match5') ms = 70;
  else if (kind === 'special') ms = 80;
  else if (kind === 'cascade') ms = 30 + Math.min(50, Math.max(0, magnitude - 2) * 15);
  return Math.min(BEAT_PAD_CAP_MS, ms);
}

/** Camera shake intensity. Match-3 stays still. Reduced motion stays still. */
export function shakeFor(kind: JuiceKind, magnitude = 1): number {
  if (prefersReducedMotion()) return 0;
  if (kind === 'match3') return 0;
  if (kind === 'match4') return 0.0055;
  if (kind === 'match5') return 0.009;
  if (kind === 'moment') return 0.01;
  if (kind === 'special') return Math.min(0.014, 0.009 + Math.max(0, magnitude - 4) * 0.0004);
  const step = Math.max(0, magnitude - 2);
  return Math.min(0.014, 0.006 + step * 0.0025);
}

/**
 * Shake that grows with the combo. Match-3 stays still.
 * Reduced motion stays still, matching `shakeFor`.
 */
export function comboShake(kind: JuiceKind, magnitude = 1): number {
  const base = shakeFor(kind, magnitude);
  if (base <= 0) return 0;
  if (kind === 'cascade') return Math.min(0.022, base + Math.max(0, magnitude - 1) * 0.002);
  if (kind === 'match5' || kind === 'special') return Math.min(0.02, base + 0.005);
  if (kind === 'match4') return Math.min(0.016, base + 0.003);
  return base;
}

/** A short freeze on a big combo. Match-3 and reduced motion do not stop. */
export function hitStopMs(kind: JuiceKind, magnitude = 1): number {
  if (prefersReducedMotion()) return 0;
  if (kind === 'match3' || kind === 'moment') return 0;
  if (kind === 'match4') return 50;
  if (kind === 'match5' || kind === 'special') return 85;
  if (kind === 'cascade') return Math.min(110, 60 + Math.max(0, magnitude - 2) * 12);
  return 0;
}

/** Full-screen wash. Low enough that tile colors stay readable. */
export function flashAlpha(kind: JuiceKind, magnitude = 1): number {
  if (prefersReducedMotion()) return kind === 'moment' ? 0.08 : 0;
  if (kind === 'match3') return 0;
  if (kind === 'match4') return 0.36;
  if (kind === 'match5') return 0.44;
  if (kind === 'moment') return 0.22;
  if (kind === 'special') return 0.4;
  return Math.min(0.48, 0.32 + Math.max(0, magnitude - 2) * 0.04);
}

/** Darken for a signature or powerup. About 35% in full motion. */
export function momentDim(): number {
  return prefersReducedMotion() ? 0.14 : 0.35;
}

export function particleCount(base: number, kind: JuiceKind = 'match3'): number {
  const tier =
    kind === 'match5' || kind === 'cascade' ? 1.55 : kind === 'special' || kind === 'moment' ? 1.7 : kind === 'match4' ? 1.3 : 1;
  const scale = prefersReducedMotion() ? 0.4 : 1;
  return Math.max(prefersReducedMotion() ? 2 : 4, Math.round(base * tier * scale));
}

/** Sprites reused for clear-tile flights. A cascade must not allocate without a limit. */
export const FLIGHT_POOL = 24;

/** Particles launched for one clear. Reduced motion keeps a short handful. */
export function flightCap(): number {
  return prefersReducedMotion() ? 6 : 16;
}

/** One dense cloud for a big clear. Match-4 and up stay in the 40–80 range. */
export function burstCount(kind: JuiceKind): number {
  if (prefersReducedMotion()) return kind === 'match3' ? 4 : 8;
  if (kind === 'match3') return 14;
  if (kind === 'match4') return 52;
  if (kind === 'match5') return 64;
  if (kind === 'cascade') return 72;
  return 60;
}

export function scorePx(kind: JuiceKind, magnitude = 1): number {
  if (prefersReducedMotion()) {
    if (kind === 'cascade') return 72;
    if (kind === 'match4') return 64;
    if (kind === 'match5' || kind === 'special' || kind === 'moment') return 72;
    return 44;
  }
  if (kind === 'cascade') return Math.min(200, 156 + Math.max(0, magnitude - 2) * 16);
  if (kind === 'match5' || kind === 'special' || kind === 'moment') return 168;
  if (kind === 'match4') return 132;
  return 72;
}

/** Shockwave radius in design pixels. A match-4 ring spans several cells. */
export function shockwaveRadius(kind: JuiceKind): number {
  if (kind === 'match3') return 0;
  if (prefersReducedMotion()) return 110;
  if (kind === 'match4') return 260;
  if (kind === 'match5') return 320;
  if (kind === 'cascade') return 300;
  return 340;
}

export function shockwaveCount(kind: JuiceKind, magnitude = 1): number {
  if (kind === 'match3') return 0;
  if (prefersReducedMotion()) return 1;
  if (kind === 'match4') return 1;
  if (kind === 'match5' || kind === 'moment') return 2;
  if (kind === 'special') return 3;
  return magnitude >= 4 ? 3 : 2;
}

/** Tile squash for the match pop. Reduced motion keeps a smaller pop. */
export function popScale(kind: JuiceKind): { x: number; y: number } {
  if (prefersReducedMotion()) return { x: 1.16, y: 0.88 };
  if (kind === 'match5' || kind === 'special') return { x: 1.46, y: 0.7 };
  if (kind === 'match4' || kind === 'cascade') return { x: 1.36, y: 0.76 };
  return { x: 1.28, y: 0.8 };
}

export function louder(
  a: { kind: JuiceKind; magnitude: number },
  b: { kind: JuiceKind; magnitude: number },
): { kind: JuiceKind; magnitude: number } {
  return shakeFor(a.kind, a.magnitude) >= shakeFor(b.kind, b.magnitude) ? a : b;
}

/** Name plaques stay this far inside the 720-wide screen, even at the pop. */
export const BANNER_MARGIN = 28;
/** Back.easeOut travels past the resting scale. Fit the plaque to this peak. */
export const BANNER_PEAK_SCALE = 1.2;
/** Score and combo text overshoot to this scale, then settle. */
export const LABEL_PEAK_SCALE = 1.28;

const CHAR_EM = 0.66;

/** Average of the matched cells, so a ring sits on the group rather than a corner. */
export function boardCentroid(cells: readonly { r: number; c: number }[]): { r: number; c: number } | null {
  if (cells.length === 0) return null;
  let r = 0;
  let c = 0;
  for (const cell of cells) {
    r += cell.r;
    c += cell.c;
  }
  return { r: r / cells.length, c: c / cells.length };
}

/**
 * Peak scale that keeps a measured label inside the portrait stage.
 * `stage.w` (720) is the whole 390-wide phone; a label wider than that runs off the glass.
 */
export function labelPeakScale(width: number, desired = LABEL_PEAK_SCALE, viewport = 720): number {
  const maxW = viewport - BANNER_MARGIN * 2;
  if (width <= 0) return desired;
  return Math.min(desired, maxW / width);
}

/** Largest Fredoka size whose estimated width fits on screen at `peakScale`. */
export function fittedFontPx(label: string, startPx: number, peakScale: number, viewport = 720): number {
  const raw = label.trim().toUpperCase();
  const text = raw.endsWith('!') ? raw : `${raw}!`;
  const maxWidth = (viewport - BANNER_MARGIN * 2) / peakScale;
  let px = startPx;
  while (px > 28 && text.length * px * CHAR_EM > maxWidth) px -= 2;
  return px;
}

/** Plaque width and type size for a powerup or signature name. */
export function bannerLayout(
  label: string,
  viewport = 720,
): { fontPx: number; width: number; peakWidth: number } {
  const fontPx = fittedFontPx(label, 64, BANNER_PEAK_SCALE, viewport);
  const raw = label.trim().toUpperCase();
  const text = raw.endsWith('!') ? raw : `${raw}!`;
  const maxWidth = (viewport - BANNER_MARGIN * 2) / BANNER_PEAK_SCALE;
  const textW = text.length * fontPx * CHAR_EM;
  const width = Math.min(maxWidth, Math.max(300, textW + 80));
  return { fontPx, width, peakWidth: width * BANNER_PEAK_SCALE };
}
